import { useState, useEffect, useRef } from 'react';
import { 
  FiMessageSquare, 
  FiSend, 
  FiRefreshCw, 
  FiAlertCircle, 
  FiSearch
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import diagramApi from '../../services/diagramApi';
import { useDiagram } from '../../contexts/DiagramContext';
import { usePersona } from '../../contexts/PersonaContext';
import { getSamplePrompts, WELCOME_TEXT, INPUT_PLACEHOLDER, ERROR_MESSAGES, INITIAL_QUERY_TEMPLATE, EXAMPLE_DIAGRAMS } from '../../utils/templates';
import './AiChat.css';

// AI Chat component
const AiChat = ({ isOpen, onClose, initialQuery = '', onCodeUpdate = null, apiKeyRequired = false, onCustomizePersona = null }) => {
  const { code } = useDiagram();
  const { personalizedPrompts, isLoading: isLoadingPrompts, promptsJustLoaded } = usePersona();
  const [messages, setMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [apiAvailable, setApiAvailable] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const [showResearchMode, setShowResearchMode] = useState(false); // Add state for research mode
  const [samplePrompts, setSamplePrompts] = useState([]);
  const [provider, setProvider] = useState('openai');

  // State for Research
  const [researchQuery, setResearchQuery] = useState('');
  const [researchResult, setResearchResult] = useState('');
  const [isResearching, setIsResearching] = useState(false);
  const [researchError, setResearchError] = useState(null);

  const messagesEndRef = useRef(null);
  const initialized = useRef(false);
  const inputRef = useRef(null);
  
  // Get sample prompts - either personalized or default
  const promptsToDisplay = getSamplePrompts(personalizedPrompts);
  
  // Handle loading a diagram example directly
  const handleDiagramExampleClick = (diagramCode) => {
    if (onCodeUpdate && diagramCode) {
      onCodeUpdate(diagramCode);
      // Optional: Show a success message
      const successMessage = { 
        role: 'assistant', 
        content: "Diagram loaded successfully! You can now edit it directly or ask me to help you modify it."
      };
      setMessages([successMessage]);
      setChatHistory([successMessage]);
      setShowWelcomeScreen(false);
    }
  };
  
  // Render sample prompts with loading state
  const renderSamplePrompts = () => {
    if (isLoadingPrompts) {
      return (
        <div className="ai-chat-loading-prompts">
          <div className="loading-spinner-small"></div>
          <p>Generating personalized prompts based on your settings...</p>
        </div>
      );
    }
    
    // Helper function to truncate text
    const truncateText = (text, maxLength = 50) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };
    
    return (
      <div className={`ai-chat-sample-prompts ${promptsJustLoaded ? 'personalized-loaded' : ''}`}>
        {/* Diagram Examples Section - Now positioned first */}
        <h3 className="ai-chat-examples-header">Example Diagrams:</h3>
        <div className="ai-chat-diagram-examples">
          {EXAMPLE_DIAGRAMS.map((diagram, index) => (
            <div 
              key={`diagram-${index}`} 
              className="ai-chat-example-card"
              onClick={() => handleDiagramExampleClick(diagram.code)}
              title={diagram.description} 
            >
              <h4>{diagram.title}</h4>
              <p className="ai-chat-example-type">{diagram.type}</p>
              <p className="ai-chat-truncated-description">{diagram.description}</p>
            </div>
          ))}
        </div>
        
        {/* Prompts Section - Now positioned second */}
        <h3 className="ai-chat-prompts-header">{personalizedPrompts.length > 0 ? 'Your personalized prompts:' : 'Suggested Prompts:'}</h3>
        <div className="ai-chat-prompts-container">
          {promptsToDisplay.map((prompt, index) => (
            <div 
              key={index} 
              className={`ai-chat-prompt-card ${personalizedPrompts.length > 0 ? 'personalized' : ''}`}
              onClick={() => handleSamplePromptClick(prompt.prompt)}
              title={prompt.prompt} // Full prompt text shown on hover
            >
              <h4>{prompt.title}</h4>
              <p className="ai-chat-truncated-prompt">{truncateText(prompt.prompt)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Initialize on first render
  useEffect(() => {
    console.log("AiChat component - isOpen:", isOpen);
    
    // Only initialize once on first render
    if (!initialized.current) {
      initialized.current = true;
      
      // Start with the welcome screen
      setMessages([]);
      setChatHistory([]);
      setShowWelcomeScreen(true);
      
      // Check API health
      checkApiHealth();
    }
  }, []); // Empty dependency array means this only runs once on mount
  
  // Check API health
  const checkApiHealth = async () => {
    try {
      const isHealthy = await diagramApi.checkApiHealth();
      setApiAvailable(isHealthy);
    } catch (error) {
      console.error('Error checking API health:', error);
      setApiAvailable(false);
    }
  };
  
  // Update input value when initialQuery changes, but don't reset the chat
  useEffect(() => {
    // This function no longer populates the input field with initialQuery
    // The chatbox remains empty for human input
  }, [initialQuery, showWelcomeScreen]);
  
  // Improved auto-scroll to bottom of messages
  useEffect(() => {
    // Scroll to bottom whenever messages change or loading state changes
    if (messagesEndRef.current) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [messages, isLoading]); // Add isLoading as a dependency to scroll when loading state changes

  // Reset chat function
  const resetChat = (query = '') => {
    // Clear all chat messages and history
    setMessages([]);
    setChatHistory([]);
    
    // Clear research data
    setResearchQuery('');
    setResearchResult('');
    setResearchError(null);
    
    // Clear the input field
    setInputValue('');
    
    // Show the welcome screen with templates
    setShowWelcomeScreen(true);
    setShowResearchMode(false);
  };
  
  // Handle API response and extract code if present
  const handleApiResponse = async (messageHistory, currentCode = null) => {
    if (!apiAvailable) {
      setIsLoading(false);
      const errorMessage = { 
        role: 'assistant', 
        content: ERROR_MESSAGES.apiConnection
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setChatHistory(prev => [...prev, errorMessage]);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Filter out system messages for the API request
      const filteredMessages = messageHistory.filter(msg => msg.role !== 'system');
      
      // Prepare options based on selected provider
      const options = {
        provider
      };
      
      // Send request to API
      const response = await diagramApi.sendChatMessage(filteredMessages, currentCode, options);
      
      // Add assistant response to messages
      const assistantMessage = { role: 'assistant', content: response.message };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update chat history
      setChatHistory(prev => [...prev, assistantMessage]);
      
      // If there's Mermaid code in the response and we have a callback, update the diagram
      if (response.mermaid_code && onCodeUpdate) {
        // Process the code to ensure parentheses are properly escaped and markdown markers are removed
        const processedCode = diagramApi.processMermaidCode(response.mermaid_code.code);
        
        // Only update if we have valid code
        if (processedCode && processedCode.trim()) {
          onCodeUpdate(processedCode);
        }
      }
    } catch (error) {
      console.error('Error getting response from API:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: ERROR_MESSAGES.apiError
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;
    
    // Add user message
    const userMessage = { role: 'user', content: inputValue };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    
    // Update chat history
    setChatHistory(prev => [...prev, userMessage]);
    
    setShowWelcomeScreen(false);
    
    // Get current code from the last assistant message that contains code
    let currentCode = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        const extractedCode = diagramApi.extractMermaidCode(messages[i].content);
        if (extractedCode) {
          // Process the extracted code to remove markdown markers
          currentCode = diagramApi.processMermaidCode(extractedCode);
          break;
        }
      }
    }
    
    // No longer use initialQuery as a fallback
    
    // Send to API
    handleApiResponse(updatedMessages, currentCode);
    
    // Clear input
    setInputValue('');
  };

  const handlePromptSelect = (prompt) => {
    if (isLoading) return;
    
    // Instead of sending the message, just set it as the input value
    setInputValue(prompt);
    
    // Keep the welcome screen visible
    setShowWelcomeScreen(true);
  };

  const handleSamplePromptClick = (prompt) => {
    setInputValue(prompt);
    // Automatically send the message when a sample prompt is clicked
    // Ensure the welcome screen is hidden before sending
    setShowWelcomeScreen(false);

    // We need to simulate the message being sent immediately after setting the input
    // Use a slight delay to allow state update
    setTimeout(() => {
       const userMessage = { role: 'user', content: prompt };
       const updatedMessages = [...messages, userMessage];
       setMessages(updatedMessages);
       setChatHistory(prev => [...prev, userMessage]);
       
       // Send API request immediately
       handleApiResponse(updatedMessages, null); // Start with null code for sample prompts
       
       // Clear input field after sending
       setInputValue('');
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // --- Research Functions ---
  const toggleResearchMode = () => {
    setShowResearchMode(!showResearchMode);
    setShowWelcomeScreen(false);
    setResearchQuery(''); // Clear previous query
    setResearchResult(''); // Clear previous result
    setResearchError(null); // Clear previous error
  };

  const closeResearchMode = () => {
    setShowResearchMode(false);
  };

  const handleResearchSubmit = async () => {
    if (!researchQuery.trim() || isResearching) return;

    setIsResearching(true);
    setResearchError(null);
    setResearchResult(''); // Clear previous result

    try {
      const response = await diagramApi.sendResearchRequest(researchQuery);
      setResearchResult(response.result);
    } catch (error) {
      console.error('Error during research request:', error);
      setResearchError(error.message || 'An unknown error occurred during research.');
      setResearchResult(''); // Ensure result is cleared on error
    } finally {
      setIsResearching(false);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className={`ai-chat-container ${isOpen ? 'open' : 'closed'}`}>
      {/* Header with controls */}
      <div className="ai-chat-header">
        <div className="ai-chat-provider-selector">
          <label htmlFor="provider-select">Provider:</label>
          <select 
            id="provider-select" 
            value={provider} 
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="openai">OpenAI</option>
            <option value="gemini">Gemini</option>
          </select>
        </div>
        <button onClick={resetChat} className="ai-chat-reset-button" title="Reset Chat">
          <FiRefreshCw />
        </button>
      </div>
      
      {/* Mode navigation bar */}
      <div className="ai-chat-mode-nav">
        <button 
          onClick={() => {
            setShowWelcomeScreen(true);
            setShowResearchMode(false);
          }}
          className={`ai-chat-mode-button ${showWelcomeScreen ? 'active' : ''}`}
          aria-pressed={showWelcomeScreen}
        >
          <FiMessageSquare /> Templates
        </button>
        <button 
          onClick={() => {
            setShowWelcomeScreen(false);
            setShowResearchMode(false);
          }}
          className={`ai-chat-mode-button ${!showWelcomeScreen && !showResearchMode ? 'active' : ''}`}
          aria-pressed={!showWelcomeScreen && !showResearchMode}
        >
          <FiMessageSquare /> Chat
        </button>
        <button 
          onClick={() => {
            setShowWelcomeScreen(false);
            setShowResearchMode(true);
          }}
          className={`ai-chat-mode-button ${showResearchMode ? 'active' : ''}`}
          aria-pressed={showResearchMode}
          disabled={!apiAvailable}
        >
          <FiSearch /> Research
        </button>
      </div>
      
      {/* Message display area */}
      <div className="ai-chat-messages">
        {showWelcomeScreen ? (
          <div className="ai-chat-welcome">
            <h2>{WELCOME_TEXT.title}</h2>
            <p>{WELCOME_TEXT.description}</p>
            {renderSamplePrompts()}
          </div>
        ) : showResearchMode ? (
          <div className="ai-chat-research">
            <div className="ai-chat-research-header">
              <h2>Research Mode</h2>
            </div>
            <div className="ai-chat-research-content">
              <textarea
                placeholder="Enter your research topic or question..."
                value={researchQuery}
                onChange={(e) => setResearchQuery(e.target.value)}
                rows={4}
                disabled={isResearching}
              />
              <button 
                onClick={handleResearchSubmit}
                disabled={!researchQuery.trim() || isResearching}
                className="ai-chat-research-submit-button"
              >
                {isResearching ? 'Researching...' : 'Start Research'}
              </button>
              
              {isResearching && (
                <div className="ai-chat-loading-spinner">
                  <div className="loading-spinner-small"></div> Researching...
                </div>
              )}
              
              {researchError && (
                <div className="ai-chat-error-message">
                  <FiAlertCircle /> {researchError}
                </div>
              )}
              
              {researchResult && (
                <div className="ai-chat-research-result">
                  <h3>Research Result:</h3>
                  <ReactMarkdown
                    children={researchResult}
                    components={{
                      code({node, inline, className, children, ...props}) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            children={String(children).replace(/\n$/, '')}
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          />
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        )
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={`ai-chat-message ${msg.role}`}>
              <div className="message-content">
                <ReactMarkdown
                  children={msg.content}
                  components={{
                    code({node, inline, className, children, ...props}) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          children={String(children).replace(/\n$/, '')}
                          style={vscDarkPlus}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        />
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      )
                    }
                  }}
                />
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="ai-chat-loading-indicator">
            <div className="loading-spinner-small"></div> Thinking...
          </div>
        )}
        <div ref={messagesEndRef} /> {/* Invisible element to scroll to */}
      </div>
      
      {/* Input area */}
      <div className="ai-chat-input-area">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={INPUT_PLACEHOLDER}
          rows={inputValue.split('\n').length > 3 ? 4 : Math.max(1, inputValue.split('\n').length)} // Dynamic rows
          disabled={isLoading}
        />
        <button 
          onClick={handleSendMessage} 
          className="ai-chat-send-button" 
          disabled={isLoading || !inputValue.trim() || !apiAvailable}
        >
          <FiSend />
        </button>
      </div>

      {/* API Availability Status */}
      {!apiAvailable && (
        <div className="ai-chat-api-status error">
          <FiAlertCircle /> API is currently unavailable. Please check the backend worker.
        </div>
      )}
    </div>
  );
};

export default AiChat; 